import { useEffect } from 'react';
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom';
import {
  FilterConfigsDto,
  NavigationMenuItemDto,
  NavigationPageItemDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchNavigationCustomization,
  updateNavigationCustomization,
} from '../api/portalGateway/navigationCustomization';
import { NO_ROLE_ID } from '../api/portalGatewayClient';
import { useStore } from '../store';
import { Filter } from '../store/states/filterTypes';
import { filtersToKeyValuePairs } from '../utils/filtersToKeyValuePairs';
import { prependRoleId } from '../utils/prependRoleId';
import { pageWithRoleLink } from '../utils/urlPath';

export type UseSyncFiltersToSearchParams = {
  filtersToSet: ParamKeyValuePair[];
  allFilterUrlKeys: string[];
  navigationData?: NavigationMenuItemDto | NavigationPageItemDto;
  navItemId: string | null;
  pageData?: PageDto;
};

/**
 * Generic hook to synchronize filters with search parameters.
 * Not intended to be used directly, but rather as a helper for specific synchronization hooks
 * (e.g. useSyncPageFiltersToSearchParams and useSyncTableFiltersToSearchParams)
 */
export function useSyncFiltersToSearchParams({
  filtersToSet,
  allFilterUrlKeys,
  navigationData,
  navItemId,
  pageData,
}: UseSyncFiltersToSearchParams) {
  const roleId = useStore(useShallow((state) => state.activeUserRole?.id));

  const queryClient = useQueryClient();
  const updateMutation = useUpdateMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    // only trigger syncback after filters are initialised and loaded
    if (filtersToSet != null) {
      // build updated url params based on set filters
      const nextUrlSearchParams = new URLSearchParams(searchParams);

      // Remove all filters from the url
      for (const fc of allFilterUrlKeys) {
        nextUrlSearchParams.delete(fc);
      }

      // Add new filters to the url
      for (const [key, value] of filtersToSet) {
        nextUrlSearchParams.append(key, value);
      }

      // update dynamic nav item
      if (
        navigationData != null &&
        navigationData.type === 'navigation-page-item' &&
        pageData?.type === 'detailPage'
      ) {
        // build new url
        let href = pageWithRoleLink(roleId ?? NO_ROLE_ID, {
          pageType: 'detailPage',
          slug: navigationData.completeSlug,
        });

        const nextUrlSearchString = nextUrlSearchParams.toString();
        if (nextUrlSearchString.length > 0) {
          href = `${href}?${nextUrlSearchString}`;
        }
        // check if we already have a dynamic navigation item
        const previousNavigationCustomization = queryClient.getQueryData(
          getQueryOptions().queryKey
        );
        const previousItem =
          previousNavigationCustomization?.data.dynamicNavigationItems.find(
            (nit) => nit.id === navItemId
          );
        // proceed to update only if we have an item and required data
        if (
          navItemId != null &&
          href != null &&
          previousItem != null &&
          !equalHref(previousItem.href, href) &&
          !updateMutation.isPending
        ) {
          // update the dynamic navigation item
          updateMutation.mutate({
            data: {
              id: navItemId,
              label: previousItem.label,
              href,
              icon: previousItem.icon,
            },
          });
        }
      }

      if (
        // compare number and values of next params to current ones
        nextUrlSearchParams.toString().length !==
          searchParams.toString().length ||
        [...nextUrlSearchParams.entries()].some(
          ([key, value]) => !searchParams.getAll(key).includes(value)
        )
      ) {
        // if a difference was found, update the url
        setSearchParams([...nextUrlSearchParams.entries()], {
          replace: true,
        });
      }
    }
  }, [
    allFilterUrlKeys,
    navigationData,
    navItemId,
    pageData,
    queryClient,
    roleId,
    searchParams,
    setSearchParams,
    updateMutation,
    filtersToSet,
  ]);
}

/**
 * Checks if filters are initialized and loaded in the store and returns the filters in key-value pair format.
 * If a transformKey function is provided, it will be used to transform the key before adding it to the resulting array.
 */
export function useCurrentStoreFilters(
  filterDescriptors: FilterDescriptor[],
  transformKey: (instanceId: string, key: string) => string = (_, key) => key
) {
  const filterData = useStore(useShallow((state) => state.filterData));
  const filtersToSet: ParamKeyValuePair[] = [];
  for (const { instanceId, config } of filterDescriptors) {
    if (instanceId == null) {
      continue;
    }

    const filtersForInstance = filterData[instanceId];

    if (
      filtersForInstance != null &&
      filtersForInstance.meta.initialised &&
      filtersForInstance.meta.loaded
    ) {
      const configuredFilters = config?.reduce((acc, fc) => {
        if (filtersForInstance.visualisedFilter[fc.key] != null) {
          acc[transformKey(instanceId, fc.key)] =
            filtersForInstance.visualisedFilter[fc.key];
        }
        return acc;
      }, {} as Filter);

      if (configuredFilters != null) {
        filtersToSet.push(...filtersToKeyValuePairs(configuredFilters));
      }
    }
  }

  return filtersToSet;
}

// Checks if two hrefs are equal.
// They are considered equal if the pathname matches and the search parameters are equal regardless of the order.
function equalHref(a: string, b: string) {
  const urlA = new URL(a, globalThis.location.origin);
  const urlB = new URL(b, globalThis.location.origin);

  return (
    urlA.pathname === urlB.pathname &&
    urlA.searchParams.size === urlB.searchParams.size &&
    [...urlA.searchParams.entries()].every(([key, value]) =>
      urlB.searchParams.getAll(key).includes(value)
    )
  );
}

type FilterDescriptor = {
  instanceId: string | null;
  config?: FilterConfigsDto['filters'];
};

function getQueryOptions() {
  return queryOptions({
    queryKey: prependRoleId(['navigationCustomization']),
    queryFn: fetchNavigationCustomization,
  });
}

function useUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNavigationCustomization,
    onMutate: async (newNavItem) => {
      await queryClient.cancelQueries(getQueryOptions());
      const previousNavigationCustomization = queryClient.getQueryData(
        getQueryOptions().queryKey
      );

      // optimistically update to the new value
      queryClient.setQueryData(getQueryOptions().queryKey, (old) =>
        old
          ? {
              ...old,
              data: {
                dynamicNavigationItems: old.data.dynamicNavigationItems.map(
                  (navItem) =>
                    navItem.id === newNavItem.data.id
                      ? { ...navItem, ...newNavItem.data }
                      : navItem
                ),
              },
            }
          : undefined
      );

      return { previousNavigationCustomization };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(
        getQueryOptions().queryKey,
        context?.previousNavigationCustomization
      );
    },
  });
}
